import { z } from 'zod';
import { cuid, paginacao } from './primitives.js';

/**
 * Lote pendente de atualizacao de custos e marcas "custo atualizado - revisar preco" (Sessao 5, E10; DESENHO c).
 *
 * Em `modoAtualizacaoCusto = COM_APROVACAO` (padrao) a mudanca de CPM (`POST /centros-custo/cpm`) ou de custo de aquisicao
 * (`POST /custos-aquisicao`) NAO grava o fato: responde 202 com `EnfileiradoNoLoteResponse` e entra no UNICO lote aberto da
 * empresa (CALCULANDO -> PENDENTE em segundo plano). `lote.decidir` aplica (grava tudo de uma vez) ou descarta. Em `AUTOMATICO`
 * (ou empresa sem parametros vigentes) grava na hora, como antes, e registra um lote ja APLICADO com o impacto (`loteId` na resposta).
 * Com lote aberto, AUTOMATICO recusa a gravacao: 409 `LOTE_ABERTO`.
 */

export const loteStatus = z.enum(['CALCULANDO', 'PENDENTE', 'APLICADO', 'DESCARTADO']);
export type LoteStatusDto = z.infer<typeof loteStatus>;
export const modoAtualizacao = z.enum(['AUTOMATICO', 'COM_APROVACAO']);

export const listarLotesSchema = paginacao.extend({ status: loteStatus.optional() });
export type ListarLotesQuery = z.infer<typeof listarLotesSchema>;

export const descartarLoteSchema = z.object({ motivo: z.string().trim().min(1).max(300) });
export type DescartarLoteDto = z.infer<typeof descartarLoteSchema>;

export const tipoImpacto = z.enum(['FICHA', 'AQUISICAO', 'PRECO', 'PROPOSTA']);
export type TipoImpacto = z.infer<typeof tipoImpacto>;

/** GET /lotes/:id/impactos. `ordenar`: `delta` = maior |variacao| primeiro (padrao), `codigo`, `lucro` (menor lucro depois primeiro). */
export const listarImpactosSchema = paginacao.extend({
  tipo: tipoImpacto.default('FICHA'),
  ordenar: z.enum(['delta', 'codigo', 'lucro']).default('delta'),
  /** So cascata (true) ou so diretas (false): FICHA. */
  viaCascata: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  /** PRECO: so chaves cujo praticado atual ficaria abaixo da meta (true) / ou nao (false) depois do lote. */
  abaixoDaMeta: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  /** PRECO: so chaves abaixo do minimo depois do lote. */
  abaixoDoMinimo: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  /** PROPOSTA: so as que passam do limiar de alerta. */
  alerta: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  tabelaId: cuid.optional(),
  /** Trecho do codigo ou da descricao do item. */
  q: z.string().trim().min(1).max(100).optional(),
});
export type ListarImpactosQuery = z.infer<typeof listarImpactosSchema>;

export interface LoteResumoResponse {
  id: string;
  numero: number;
  status: LoteStatusDto;
  modo: z.infer<typeof modoAtualizacao>;
  dataReferencia: string;
  /** Tipos de mudanca do lote e a quantidade de cada um. */
  mudancas: { cpm: number; custoAquisicao: number };
  criadoPorId: string | null;
  criadoPorNome: string | null;
  criadoEm: string;
  calculadoEm: string | null;
  aplicadoEm: string | null;
  aplicadoPorNome: string | null;
  descartadoEm: string | null;
  descartadoPorNome: string | null;
  motivoDescarte: string | null;
  /** Mensagem do calculo que falhou (o lote fica PENDENTE sem relatorio completo; `POST :id/recalcular` tenta de novo). */
  erroCalculo: string | null;
  /** Contagens do relatorio (nulo enquanto CALCULANDO). */
  resumo: RelatorioResumo | null;
}

export interface FaixaDistribuicao {
  /** Rotulo da faixa de variacao do custo: `< -10`, `-10..-5`, `-5..-1`, `-1..1`, `1..5`, `5..10`, `> 10` (em %). */
  faixa: string;
  quantidade: number;
}

export interface RelatorioResumo {
  /** Itens cujo custo mudou: com ficha (incl. cascata) e mercadorias com custo de aquisicao novo. */
  fichasQueMudam: number;
  viaCascata: number;
  mercadoriasQueMudam: number;
  /** Itens que passam a ficar sem custo (ciclo, CPM ausente) ou voltam a ter. */
  semCusto: number;
  distribuicaoDelta: FaixaDistribuicao[];
  chavesDePreco: number;
  sugeridoMuda: number;
  /** Praticado atual ficaria abaixo da meta / do minimo com o custo novo. `novos` = nao estava abaixo antes. */
  praticadosAbaixoDaMeta: number;
  praticadosAbaixoDaMetaNovos: number;
  praticadosAbaixoDoMinimo: number;
  propostasAbertas: number;
  propostasAfetadas: number;
  propostasComAlerta: number;
}

export interface MudancaLoteResponse {
  id: string;
  tipo: 'CPM' | 'CUSTO_AQUISICAO';
  /** CPM */
  centroId?: string | null;
  centroCodigo?: string | null;
  competencia?: string | null;
  /** R$/min (10 casas). */
  valor?: string | null;
  minutosDisponiveis?: string | null;
  custoTotal?: string | null;
  origem?: 'MANUAL' | 'RATEIO' | 'IMPORTADO_XARP' | null;
  motivo?: string | null;
  /** CUSTO_AQUISICAO: item, entrada do motor e vigencia proposta (a efetiva sera max(proposta, dia da aplicacao)). */
  itemId?: string | null;
  itemCodigo?: string | null;
  aquisicao?: unknown;
  validFrom?: string | null;
  criadoPorNome: string | null;
  criadoEm: string;
}

export interface LoteDetalheResponse extends LoteResumoResponse {
  mudancasLista: MudancaLoteResponse[];
  /** Top 20 por |variacao| do custo (FICHA e AQUISICAO). */
  top: ImpactoResponse[];
}

export interface ImpactoResponse {
  id: string;
  tipo: TipoImpacto;
  itemId: string | null;
  itemCodigo: string | null;
  itemDescricao: string | null;
  fichaId: string | null;
  /** O custo mudou so porque uma subficha mudou (cascata de semiacabados, DP3). */
  viaCascata: boolean;
  nivelMaximo: number | null;
  tabelaId: string | null;
  tabelaCodigo: string | null;
  condicaoId: string | null;
  condicaoCodigo: string | null;
  orcamentoId: string | null;
  orcamentoNumero: number | null;
  custoAntes: string | null;
  custoDepois: string | null;
  /** (depois - antes) / antes, fracao com 8 casas; nulo quando o custo antes era 0. */
  deltaPct: string | null;
  sugeridoAntes: string | null;
  sugeridoDepois: string | null;
  praticado: string | null;
  lucroAntes: string | null;
  lucroDepois: string | null;
  metaLucro: string | null;
  minimoLucro: string | null;
  abaixoDaMetaAntes: boolean | null;
  abaixoDaMetaDepois: boolean | null;
  abaixoDoMinimoDepois: boolean | null;
  alerta: boolean | null;
}

export interface ImpactosPaginaResponse {
  itens: ImpactoResponse[];
  total: number;
  limit: number;
  offset: number;
}

/** Resposta 202 de POST /centros-custo/cpm e POST /custos-aquisicao em COM_APROVACAO: nada foi gravado, esta no lote. */
export interface EnfileiradoNoLoteResponse {
  modo: 'COM_APROVACAO';
  enfileiradas: number;
  /** CPM: alteracoes iguais ao que ja vale (nao entram no lote). */
  semMudanca: number;
  lote: LoteResumoResponse;
}

// ---------------------------------------------------------------------------------------------
// Marcas "custo atualizado - revisar preco"
// ---------------------------------------------------------------------------------------------

export const listarMarcasSchema = paginacao.extend({
  tabelaId: cuid.optional(),
  itemId: cuid.optional(),
  condicaoId: cuid.optional(),
  /** Ordem: maior variacao do sugerido primeiro (padrao) ou codigo. */
  ordenar: z.enum(['delta', 'codigo']).default('delta'),
});
export type ListarMarcasQuery = z.infer<typeof listarMarcasSchema>;

export const dispensarMarcaSchema = z.object({ motivo: z.string().trim().min(1).max(300).optional() });
export type DispensarMarcaDto = z.infer<typeof dispensarMarcaSchema>;

/**
 * Dispensa em lote (Sessao 37-16). Sem `confirmar` e so a PREVIA (a contagem, nada grava). Com `confirmar: true` o cliente manda o `esperado`
 * da previa; se a contagem de agora for outra, responde 409 e nada e dispensado. Filtros se combinam com E; sem nenhum filtro recusa (400):
 * dispensar tudo e pedido explicito (`todas: true`).
 * - `variacaoMinPct` / `variacaoMaxPct`: modulo da variacao do sugerido desde o praticado, em % (10 = 10%).
 * - `abaixoDoMinimo` / `abaixoDaMeta`: true so as marcas cujo praticado de hoje esta nessa faixa; false so as que nao estao.
 */
export const dispensarMarcasLoteSchema = z
  .object({
    tabelaId: cuid.optional(),
    condicaoId: cuid.optional(),
    categoriaId: cuid.optional(),
    variacaoMinPct: z.coerce.number().min(0).max(100000).optional(),
    variacaoMaxPct: z.coerce.number().min(0).max(100000).optional(),
    abaixoDoMinimo: z.boolean().optional(),
    abaixoDaMeta: z.boolean().optional(),
    todas: z.boolean().optional(),
    motivo: z.string().trim().min(1).max(300),
    confirmar: z.boolean().default(false),
    esperado: z.number().int().min(0).optional(),
  })
  .superRefine((v, ctx) => {
    const semFiltro = v.tabelaId === undefined && v.condicaoId === undefined && v.categoriaId === undefined && v.variacaoMinPct === undefined && v.variacaoMaxPct === undefined && v.abaixoDoMinimo === undefined && v.abaixoDaMeta === undefined;
    if (semFiltro && v.todas !== true) ctx.addIssue({ code: 'custom', path: ['tabelaId'], message: 'Informe ao menos um filtro (ou marque todas as marcas)' });
    if (v.variacaoMinPct !== undefined && v.variacaoMaxPct !== undefined && v.variacaoMinPct > v.variacaoMaxPct) ctx.addIssue({ code: 'custom', path: ['variacaoMinPct'], message: 'A variacao minima nao pode passar da maxima' });
    if (v.confirmar && v.esperado === undefined) ctx.addIssue({ code: 'custom', path: ['esperado'], message: 'Confirme com a contagem da previa' });
  });
export type DispensarMarcasLoteDto = z.infer<typeof dispensarMarcasLoteSchema>;
export interface DispensarMarcasLoteResponse {
  confirmado: boolean;
  /** Marcas que casam com os filtros agora (previa) ou foram dispensadas (confirmado). */
  total: number;
  /** Quantas das abertas da empresa ficaram de fora. */
  restantes: number;
  /** Marcas ignoradas por nao dar para avaliar a faixa (sem custo, sem praticado ou tabela sem vigencia) quando o filtro de faixa foi pedido. */
  naoAvaliadas: number;
}

/**
 * Marca aberta de uma chave item x tabela x condicao: o sugerido mudou por custo e o praticado NAO mudou. Na lista e na
 * formacao o `delta` e SEMPRE ao vivo: sugerido de hoje (2 casas) menos `sugeridoAntes` (o sugerido quando o praticado
 * foi definido/ultima marca resolvida). Sem `custo.ver` (VENDEDOR, LEITURA) so chegam `desde`, `sugeridoDepois`, `delta` e
 * `deltaPct`: a marca e o novo sugerido, nunca o lucro nem a origem.
 */
export interface MarcaRevisaoResponse {
  /** Para dispensar (`marca.dispensar`); omitido para quem nao pode. */
  id?: string;
  desde: string;
  /** Sugerido (2 casas) quando a marca nasceu e o novo sugerido. */
  sugeridoAntes: string;
  sugeridoDepois: string;
  /** sugerido de hoje - sugeridoAntes (2 casas) e a variacao em fracao (4 casas); nulo se sugeridoAntes = 0. */
  delta: string;
  deltaPct: string | null;
  /** So com custo.ver. */
  origem?: 'LOTE' | 'FICHA_ESCOLHIDA';
  loteId?: string | null;
  praticado?: string | null;
  lucroAntes?: string | null;
  lucroDepois?: string | null;
}

export interface MarcaRevisaoLinhaResponse extends MarcaRevisaoResponse {
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  tabelaId: string;
  tabelaCodigo: string;
  condicaoId: string;
  condicaoCodigo: string;
}

export interface MarcasPaginaResponse {
  itens: MarcaRevisaoLinhaResponse[];
  total: number;
  limit: number;
  offset: number;
}
